import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { ICompanionForm } from "../../../../../../shared/interfaces/forms/exercises/ICompanionForm";
import { ValidationHelperService } from "../../../../../../core/helpers/validation-helper.service";
import { ECompanionCommunicationType } from "../../../../../../shared/enums/ECompanionCommunicationType";
import { EStroopEffectType } from "../../../../../../shared/enums/EStroopEffectType";
import { Subscription } from "rxjs";
import {
  COMPANION_DELAY_OPTIONS,
  COMPANION_PROMPT_OPTIONS,
} from "../../companion-timer-options";
import { COMPANION_COLOR_OPTIONS } from "../../companion-color-options";
import { MatTabChangeEvent } from "@angular/material/tabs";

@Component({
  selector: "app-companion-react-form",
  templateUrl: "./companion-react-form.component.html",
  styleUrls: ["./companion-react-form.component.scss"],
})
export class CompanionReactFormComponent implements OnInit, OnDestroy {
  @Input() _formGroup: FormGroup<ICompanionForm>;
  readonly delayOptions = COMPANION_DELAY_OPTIONS;
  readonly promptOptions = COMPANION_PROMPT_OPTIONS;
  readonly colorOptions = COMPANION_COLOR_OPTIONS;
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscribeToNeurocognitiveTypeChange();
    this.subscribeToReactSensorsChange();
  }

  subscribeToNeurocognitiveTypeChange(): void {
    this.subscriptions.add(
      this.neurocognitiveType.valueChanges.subscribe((newValue) => {
        // If neurocognitive is set to "Off" (0), reset all "Don't react to" colors to false
        if (newValue === 0) {
          this.resetDontReactToColors();
        }

        // Trigger validation when neurocognitive type changes
        this._formGroup.updateValueAndValidity();
      }),
    );

    // Subscribe to stroop effect type changes to trigger validation
    this.subscriptions.add(
      this.stroopEffectType.valueChanges.subscribe(() => {
        // Trigger validation when stroop effect type changes
        this._formGroup.updateValueAndValidity();
      }),
    );
  }

  subscribeToReactSensorsChange(): void {
    this.subscriptions.add(
      this.reactSensors.valueChanges.subscribe(() => {
        this._formGroup.updateValueAndValidity();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private resetDontReactToColors(): void {
    // Reset all colors in "Don't react to" to false
    for (let i = 0; i < this.dontReactTo.length; i++) {
      this.dontReactTo.controls[i].setValue(false);
    }
  }

  getSensorValue(color, type): boolean {
    return type !== 2 ? this.react[color] : this.dontReact[color];
  }

  setReact(color: number, type: number) {
    this.toggleReact(color, type);

    if (type !== 2 && this.getSensorValue(color, 2)) {
      this.toggleReact(color, 2);
    } else if (type === 2 && this.getSensorValue(color, 1)) {
      this.toggleReact(color, 1);
    }
  }

  toggleReact(color: number, type: number) {
    const typeKey = type !== 2 ? "reactTo" : "dontReactTo";
    if (typeKey === "reactTo") {
      this._formGroup.controls.reactTo.controls[color].setValue(
        !this._formGroup.get(typeKey).value[color],
      );
    } else if (typeKey === "dontReactTo") {
      this._formGroup.controls.dontReactTo.controls[color].setValue(
        !this._formGroup.get(typeKey).value[color],
      );
    }

    // Trigger validation after color selection changes
    this._formGroup.updateValueAndValidity();
  }

  setSensor(sensor: number) {
    this.reactSensors.controls[sensor].setValue(
      !this.reactSensors.controls[sensor].value,
    );
    this.isolatedReact.updateValueAndValidity();
  }

  resetReact() {
    this.reactTo.controls.forEach((control) =>
      control.setValue(false, { emitEvent: false }),
    );
    this.dontReactTo.controls.forEach((control) =>
      control.setValue(false, { emitEvent: false }),
    );
    this.reactSensors.controls.forEach((control) =>
      control.setValue(false, { emitEvent: false }),
    );
    this._formGroup.updateValueAndValidity();
  }

  onCommunicationTabChange(event: MatTabChangeEvent): void {
    this.communicationType.setValue(event.index as ECompanionCommunicationType);
    this._formGroup.updateValueAndValidity();
  }

  get reactTo(): FormArray<FormControl<boolean>> {
    return this._formGroup.get("reactTo") as any;
  }
  get dontReactTo(): FormArray<FormControl<boolean>> {
    return this._formGroup.get("dontReactTo") as any;
  }
  get delay(): FormControl<number> {
    return this._formGroup.get("delay") as any;
  }
  get timerPrompt(): FormControl<number> {
    return this._formGroup.get("timerPrompt") as any;
  }
  get communicationType(): FormControl<ECompanionCommunicationType> {
    return this._formGroup.get("communicationType") as any;
  }
  get neurocognitiveType(): FormControl<0 | 1 | 2> {
    return this._formGroup.get("neurocognitiveType") as any;
  }
  get stroopEffectType(): FormControl<EStroopEffectType> {
    return this._formGroup.get("stroopEffectType") as any;
  }
  get isolatedReact(): FormControl<boolean> {
    return this._formGroup.get("isolatedReact") as any;
  }

  get reactSensors(): FormArray<FormControl<boolean>> {
    return this._formGroup.get("activeSensors") as FormArray<
      FormControl<boolean>
    >;
  }

  get react(): boolean[] {
    return this._formGroup.get("reactTo").value;
  }

  get hasGoNoGoPromptError(): boolean {
    return (
      this._formGroup.touched &&
      this._formGroup.errors?.invalidPromptValue === true &&
      this.neurocognitiveType.value === 2
    );
  }

  get hasStroopSolidPromptError(): boolean {
    return (
      this._formGroup.touched &&
      this._formGroup.errors?.invalidPromptValue === true &&
      this.neurocognitiveType.value === 0 &&
      this.stroopEffectType.value !== EStroopEffectType.NONE
    );
  }

  get hasActiveSensorsError(): boolean {
    return (
      this._formGroup.touched &&
      this._formGroup.errors?.activeSensorsRequired === true
    );
  }

  get dontReact(): boolean[] {
    return this._formGroup.get("dontReactTo").value;
  }

  protected readonly ECompanionCommunicationType = ECompanionCommunicationType;
  protected readonly EStroopEffectType = EStroopEffectType;
  protected readonly ValidationHelperService = ValidationHelperService;
}
